import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';

// Simple in-memory cache to avoid redundant fetches for the same group
const tournamentCache = new Map<string, { count: number; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const useActiveTournamentsCount = (groupId: string) => {
    const [count, setCount] = useState<number | null>(null);

    useEffect(() => {
        if (!groupId) return;

        // Check cache first
        const cached = tournamentCache.get(groupId);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
            setCount(cached.count);
            return;
        }

        let isCancelled = false;

        // One-time fetch instead of real-time listener to avoid memory bloat
        const fetchCount = async () => {
            try {
                const q = query(
                    collection(db, 'groups', groupId, 'tournaments'),
                    where('status', '==', 'active')
                );
                const snapshot = await getDocs(q);

                if (isCancelled) return;

                const result = snapshot.size;

                // Update cache
                tournamentCache.set(groupId, { count: result, timestamp: Date.now() });
                setCount(result);
            } catch (error) {
                console.error("Error fetching active tournaments count:", error);
                setCount(0); // Fallback to 0 on error
            }
        };

        fetchCount();

        return () => { isCancelled = true; };
    }, [groupId]);

    return count;
};
